import { useState } from 'react'
import { useTranslation } from '../i18n'
import type { Test } from '../types'
import { apiDelete, apiPost } from '../hooks/useApi'

interface TestListProps {
  tests: Test[]
  loading: boolean
  projectId: string
  onTestUpdated: () => void
}

export function TestList({ tests, loading, projectId, onTestUpdated }: TestListProps) {
  const { t } = useTranslation()
  const [removing, setRemoving] = useState<string | null>(null)
  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set())
  const [batchRemoving, setBatchRemoving] = useState(false)

  const handleRemoveFromProject = async (test: Test) => {
    setRemoving(test.id)
    try {
      await apiDelete(
        `/tests/${encodeURIComponent(test.className)}/${encodeURIComponent(test.methodName)}/tag/${encodeURIComponent(projectId)}`
      )
      onTestUpdated()
    } catch (err) {
      alert(err instanceof Error ? err.message : t('error.failedToRemoveTest'))
    } finally {
      setRemoving(null)
    }
  }

  const handleToggleSelect = (testId: string) => {
    setSelectedTests((prev) => {
      const next = new Set(prev)
      if (next.has(testId)) {
        next.delete(testId)
      } else {
        next.add(testId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedTests.size === tests.length) {
      setSelectedTests(new Set())
    } else {
      setSelectedTests(new Set(tests.map((t) => t.id)))
    }
  }

  const handleBatchUnassign = async () => {
    setBatchRemoving(true)
    try {
      const testsToRemove = tests.filter((t) => selectedTests.has(t.id))
      await apiPost('/tests/bulk-untag', {
        testIds: testsToRemove.map((test) => ({
          className: test.className,
          methodName: test.methodName,
        })),
        projectId,
      })
      setSelectedTests(new Set())
      onTestUpdated()
    } catch (err) {
      alert(err instanceof Error ? err.message : t('error.failedToRemoveTests'))
    } finally {
      setBatchRemoving(false)
    }
  }

  const allSelected = tests.length > 0 && selectedTests.size === tests.length

  if (loading) {
    return (
      <div data-testid="test-list">
        <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>{t('label.testsInProject')}</h3>
        <div style={{ color: 'var(--color-gray-400)', textAlign: 'center', padding: '2rem' }}>
          {t('label.loading')}
        </div>
      </div>
    )
  }

  if (tests.length === 0) {
    return (
      <div data-testid="test-list">
        <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>{t('label.testsInProject')}</h3>
        <div style={{ color: 'var(--color-gray-400)', textAlign: 'center', padding: '2rem' }}>
          {t('empty.noTestsTagged')}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="test-list">
      <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>
        {t('label.testsInProject')} ({tests.length})
      </h3>
      <div
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid var(--color-border)',
          borderRadius: '4px',
        }}
      >
        <table className="table">
          <thead>
            <tr>
              <th
                style={{
                  width: '40px',
                  position: 'sticky',
                  top: 0,
                  background: 'var(--color-surface)',
                  zIndex: 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  aria-label={t('button.selectAll')}
                  data-testid="select-all-checkbox"
                />
              </th>
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  background: 'var(--color-surface)',
                  zIndex: 1,
                }}
              >
                {t('label.test')}
              </th>
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  background: 'var(--color-surface)',
                  zIndex: 1,
                }}
              >
                {t('label.status')}
              </th>
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  background: 'var(--color-surface)',
                  zIndex: 1,
                }}
              >
                {t('label.hours')}
              </th>
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  background: 'var(--color-surface)',
                  zIndex: 1,
                }}
              >
                {t('label.gap')}
              </th>
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  background: 'var(--color-surface)',
                  zIndex: 1,
                }}
              >
                {t('label.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {tests.map((test) => (
              <tr key={test.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedTests.has(test.id)}
                    onChange={() => handleToggleSelect(test.id)}
                    aria-label={`Select ${test.className}.${test.methodName}`}
                    data-testid={`select-test-${test.id}`}
                  />
                </td>
                <td>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    <div>{test.className}</div>
                    <div style={{ color: 'var(--color-gray-400)' }}>.{test.methodName}</div>
                  </div>
                </td>
                <td>
                  <span
                    className={`status-badge ${
                      test.status === 'PASSED'
                        ? 'status-passed'
                        : test.status === 'FAILED'
                          ? 'status-failed'
                          : test.status === 'SKIPPED'
                            ? 'status-skipped'
                            : ''
                    }`}
                  >
                    {test.status ?? t('status.unknown')}
                  </span>
                </td>
                <td>{test.budgetHours?.toFixed(1) ?? '-'}</td>
                <td>{test.gapPercentage}%</td>
                <td>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemoveFromProject(test)}
                    disabled={removing === test.id}
                    aria-label={t('misc.removeFromProject')}
                    title={t('misc.removeFromProject')}
                  >
                    <i className="pi pi-trash" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedTests.size > 0 && (
        <button
          className="btn btn-danger"
          onClick={handleBatchUnassign}
          disabled={batchRemoving}
          style={{ marginTop: '1rem' }}
          data-testid="batch-unassign-button"
          aria-label={t('button.unassign')}
          title={t('button.unassign')}
        >
          <i className="pi pi-trash" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
          {t('button.unassign')} ({selectedTests.size})
        </button>
      )}
    </div>
  )
}
